import { useMemo, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useTourGuide, TourGuideConfig } from "./useTourGuide";
import { useAutoTourGuide } from "./useAutoTourGuide";
import { useTourGuideContext } from "@root/context/tour-guide-context";
import { updateTourCompletion } from "@store/thunks/userProfile";
import { ROUTE_NAMES } from "@root/constants";
import { TourGuideType } from "@root/constants/tour-guide";
import useMediaQ from "@root/hooks/useMediaQ";

/**
 * Tour Guide Hook for Journalist Notice Page
 *
 * Guides users through the notice filter.
 */
export const useJournalistNoticeTourGuide = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const { setActiveTourModule, setCurrentTourStep } = useTourGuideContext();
  const { sm } = useMediaQ();
  const isMobile = !sm;

  const isOnJournalistNotice = pathname === ROUTE_NAMES.JOURNALIST_NOTICE;

  const config: TourGuideConfig = useMemo(
    () => ({
      moduleId: "journalistNotice",
      enabled: isOnJournalistNotice,
      steps: [
        {
          id: "journalist-notice-filter",
          targetSelector: '[data-tour-item="journalist-notice-filter"]',
          title: t("tourGuide.journalistNotice.step1.title"),
          description: t("tourGuide.journalistNotice.step1.description"),
          position: "bottom",
          offset: { x: 0, y: 15 },
          highlightPadding: 10,
        },
      ],
    }),
    [isOnJournalistNotice, t],
  );

  const baseTourGuide = useTourGuide(config);

  const handleTourComplete = () => {
    dispatch(
      updateTourCompletion({
        payload: {
          tourGuides: [{ type: TourGuideType.JournalistNotice, isDone: true }],
        },
        onSuccess: () => {},
        onError: () => {},
      }) as any,
    );
  };

  const journalistNoticeTourGuide = useAutoTourGuide({
    tourGuide: baseTourGuide,
    tourGuideType: TourGuideType.JournalistNotice,
    isOnTourPath: isOnJournalistNotice && !isMobile,
    onTourComplete: handleTourComplete,
  });

  useEffect(() => {
    if (journalistNoticeTourGuide.isActive) {
      setActiveTourModule("journalistNotice");
      setCurrentTourStep(journalistNoticeTourGuide.currentStep);
    } else {
      setActiveTourModule(null);
    }
  }, [
    journalistNoticeTourGuide.isActive,
    journalistNoticeTourGuide.currentStep,
    setActiveTourModule,
    setCurrentTourStep,
  ]);

  return {
    journalistNoticeTourGuide,
    isMobile,
  };
};
